import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Code,
    Divider,
    Flex,
    Heading,
    HStack,
    Icon,
    Tag,
    Text,
    VStack
} from '@chakra-ui/react';
import Prism from 'prismjs';
import 'prismjs/components/prism-kotlin';
import 'prismjs/components/prism-gradle';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-markup'; // for XML
import 'prismjs/themes/prism-tomorrow.css'; // Dark theme

import { CheckCircleIcon, InfoIcon, CheckIcon, CopyIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';

const CodeBlock = ({ language, children, section }) => {
    const [copiedSection, setCopiedSection] = useState(null);

    const languageMap = {
        'gradle': 'language-gradle',
        'kotlin': 'language-kotlin',
        'java': 'language-java',
        'xml': 'language-markup',
    };

    const prismLanguage = languageMap[language] || 'language-markup';

    const copyToClipboard = (code, section) => {
        navigator.clipboard.writeText(code.trim()).then(() => {
            setCopiedSection(section);
            setTimeout(() => setCopiedSection(null), 2000);
        }).catch(err => {
            console.error('Failed to copy:', err);
        });
    };

    useEffect(() => {
        Prism.highlightAll();
    }, [children]);

    return (
        <Box position="relative">
    <pre style={{ 
        backgroundColor: 'black', 
        textShadow: 'none', 
        color: 'white', 
        padding: '0px', 
        borderRadius: '8px',
        position: 'relative'
    }}>
        <Button
            position="absolute"
            top={3}
            right={3}
            size="sm"
            variant="ghost"
            colorScheme="whiteAlpha"
            onClick={() => copyToClipboard(children, section)}
            zIndex={10}
        >
            {copiedSection === section ? <CheckIcon w={3} h={3} /> : <CopyIcon w={3} h={3} />}
        </Button>
        <code className={prismLanguage} style={{
            padding: '15px',
            display: 'block',
            backgroundColor: 'black'
        }}>
            {children}
        </code>
    </pre>
</Box>
    );
};

const Kotlin = ({ data }) => {
    const navigate = useNavigate();

    useEffect(() => {
        Prism.highlightAll();
    }, []);

    let isConfigured = data?.android === 1 ? true : false;

    const handleSwiftConfigure = () => {

        navigate("/documentation/configure-push-notification", {
            state: {
                isConfigured
            }
        });
    };

    return (
        <div>
            <Box>
                <Flex justifyContent={"space-between"} alignItems={"center"}>
                    <Text fontSize={"2xl"} fontWeight={"semibold"}>Kotlin SDK Integration
                    </Text>
                    <Tag bg={"#7f52ff"} color={"white"} fontSize={"xs"}>Kotlin</Tag>
                </Flex>
                <Divider my={2} />
                <Text color="gray.600" mb={4}>
                    This document provides step-by-step instructions to integrate and initialize the SDK in your Android application.
                </Text>
                <Text color="gray.600" mb={4}>
                    <strong>Kotlin Version:</strong> 24.0 or higher
                </Text>
                <Box
                    borderRadius="8.75px"
                    border="1px solid"
                    borderColor={isConfigured ? "#00c951" : "#ff8904"}
                    bg={isConfigured ? "#f0fdf4" : "#fff7ed"}
                    p={4}
                    mb={4}
                >
                    <HStack spacing={3} align="center">
                        <Icon
                            as={isConfigured ? CheckCircleIcon : InfoIcon}
                            color={isConfigured ? "green.500" : "orange.500"}
                            boxSize={5}
                        />
                        <Box flex="1">
                            <HStack justify="space-between" align="center">
                                <Box>
                                    <Text fontSize="md" fontWeight="semibold" color={isConfigured ? "green.800" : "orange.800"} mb={1}>
                                        Push Notification {isConfigured ? "Configured" : "Not Configured"}
                                    </Text>
                                    <Text fontSize="sm" color={isConfigured ? "green.700" : "orange.700"}>
                                        {isConfigured
                                            ? "Your push notification service is successfully configured and ready to use."
                                            : "Configure your push notification service to enable real-time notifications."}
                                    </Text>
                                </Box>
                                <HStack spacing={3}>
                                    <Button
                                        bg={isConfigured ? "#00a63e" : "var(--brand-primary)"}
                                        variant={"solid"}
                                        size="sm"
                                        color={"white"}
                                        fontWeight="semibold"
                                        onClick={handleSwiftConfigure}
                                    >
                                        {isConfigured ? "Re-Configure" : "Configure"}
                                    </Button>
                                </HStack>
                            </HStack>
                        </Box>
                    </HStack>
                </Box>
            </Box>

            <VStack spacing={6} align="stretch">
                <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                    <Heading as="h3" size="md" mb={4}>1. Add Google Services Configuration</Heading>
                    <Text>Place the <Code>google-services.json</Code> file inside the <Code>'app'</Code> folder of your project.</Text>
                </Card>

                {/* Step 2 */}
                <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                    <Heading as="h3" size="md" mb={4}>2. Update Gradle Settings (Project Settings)</Heading>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontWeight="normal" mb={2}>Add the following jitpack in <Code>settings.gradle</Code></Text>
                            <CodeBlock language="gradle" section="settings-gradle">
                                {`dependencyResolutionManagement {
    repositoriesMode.set(RepositoriesMode.FAIL_ON_PROJECT_REPOS)
    repositories {
        google()
        mavenCentral()
        maven {
            url = uri("https://jitpack.io")

        }
    }
}`}
                            </CodeBlock>
                        </Box>
                    </VStack>
                </Card>

                {/* Step 3 */}
                <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                    <Heading as="h3" size="md" mb={4}>3. Update Gradle Properties</Heading>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontWeight="normal" mb={2}>Add the following jitpack auth token in <Code>settings.properties</Code></Text>
                            <CodeBlock language="gradle" section="gradle-properties">
                                {`authToken=jp_beks4ehmo9be2vnr4846o8546g`}
                            </CodeBlock>
                        </Box>
                    </VStack>
                </Card>

                {/* Step 4 */}
                <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                    <Heading as="h3" size="md" mb={4}>4. Update Gradle Files</Heading>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontWeight="medium" mb={2}>Project-Level <Code>build.gradle</Code> (Project Level)</Text>
                            <Text fontWeight="normal" mb={2}>Add the following plugin in the project-level <Code>build.gradle</Code> file:</Text>
                            <CodeBlock language="gradle" section="project-gradle">
                                {`plugins {
    id("com.google.gms.google-services") version "4.4.2" apply false
}`}
                            </CodeBlock>
                        </Box>

                        <Box>
                            <Text fontWeight="medium" mb={2}>Module-Level <Code>build.gradle</Code> (App Module)</Text>
                            <Text fontWeight="normal" mb={2}>Add the following plugin in the module-level <Code>build.gradle</Code> file:</Text>
                            <CodeBlock language="gradle" section="module-gradle">
                                {`plugins {
    id("com.google.gms.google-services")
}`}
                            </CodeBlock>
                        </Box>

                        <Box>
                            <Text fontWeight="medium" mb={2}>Dependencies</Text>
                            <Text fontWeight="normal" mb={2}>Add the required dependencies in the <Code>dependencies</Code> section:</Text>
                            <CodeBlock language="gradle" section="dependencies">
                                {`dependencies {
    implementation("com.github.dilawarabbas1:kotlin-sdk-v2:Global-21e0a159a2-2")
    implementation("com.google.firebase:firebase-messaging:23.3.1")
}`}
                            </CodeBlock>
                        </Box>
                    </VStack>
                </Card>

                <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                    <Box>
                        <Heading as="h3" size="md" mb={4}>5. Initialize SDK</Heading>
                        <Text mb={2}>Initialize the SDK after user sign in the onCreate() method of the MainActivity (like DashboardActivity) of your app.</Text>
                        <CodeBlock language="kotlin" section="initialize-sdk">
                            {`import com.xpush.sdk.NotificationManager
import com.xpush.sdk.MyFirebaseMessagingService
import com.xpush.sdk.NotificationMainActivity


val myApp = application as XpushApp
val phone = "92xxxxxxx" // User Mobile Number
myApp.initializeSDK(this,phone) // add phone number

val readMessageId = intent.getStringExtra("read_message_id")
if (readMessageId != null) {
    NotificationManager.checkMessageById(this,readMessageId)
}

override fun onNewIntent(intent: Intent) {
    super.onNewIntent(intent)

    setIntent(intent) // update current intent

    val readMessageId = intent?.getStringExtra("read_message_id")
    if (readMessageId != null) {
        NotificationManager.checkMessageById(this,readMessageId)
    }
}

// user for logout
// last login user received push
MyFirebaseMessagingService.isAppInForeground = false
NotificationManager.cleanup()`}
                        </CodeBlock>
                    </Box>
                    <Box mt={4}>
                        <Text fontWeight="medium" my={3}>Foreground and Background Tracking</Text>
                        <Text>Add the following code in your <Code>onResume</Code>, <Code>onPause</Code>, and <Code>onDestroy</Code> methods(in the SDK initializer activity of your app) to track app state:</Text>
                        <CodeBlock language="kotlin" section="tracking">
                            {`override fun onResume() {
    super.onResume()
    // Identify app is in foreground
    MyFirebaseMessagingService.isAppInForeground = true
}

override fun onPause() {
    super.onPause()
    // Identify app is in background
    MyFirebaseMessagingService.isAppInForeground = false
}

override fun onDestroy() {
    super.onDestroy()
    // Cleanup Xpush Service
    NotificationManager.cleanup()
}`}
                        </CodeBlock>
                    </Box>
                </Card>

                {/* Step 6 */}
                <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                    <Box>
                        <Heading as="h3" size="md" mb={4}>6. UI Implementation</Heading>
                        <Text fontWeight="medium" mb={2}>Add Notification Counter in Activity Layout</Text>
                        <Text fontWeight="normal" mb={2}>Add the following <Code>TextView</Code> in your XML layout file to display notification counts: (adjust as per your layout)</Text>
                        <CodeBlock language="xml" section="textview">
                            {`<TextView
    android:id="@+id/notification_counter"
    android:layout_width="32dp"
    android:layout_height="32dp"
    android:text="0"
    android:textSize="16sp"
    android:textColor="#FFFFFF"
    android:gravity="center"
    android:background="@drawable/circle_background"
    android:layout_alignParentEnd="true"
    android:layout_marginTop="16dp"
    android:layout_marginEnd="16dp"/>`}
                        </CodeBlock>
                    </Box>
                    <Box mt={4}>
                        <Text fontWeight="medium" mb={4}>Add Circle Background in <Code>res/drawable/circle_background.xml</Code></Text>
                        <CodeBlock language="xml" section="circle-background">
                            {`<shape xmlns:android="http://schemas.android.com/apk/res/android" android:shape="oval">
    <solid android:color="#FF0000"/>
    <size android:width="32dp" android:height="32dp"/>
</shape>`}
                        </CodeBlock>
                    </Box>
                </Card>

                {/* Step 7 */}
                <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                    <Box>
                        <Heading as="h3" size="md" my={3}>7. Modify AndroidManifest.xml</Heading>
                        <Text fontWeight="medium" mb={2}>Permissions</Text>
                        <Text fontWeight="normal" mb={2}>Add the required permissions to your manifest file:</Text>
                        <CodeBlock language="xml" section="permissions">
                            {`<uses-permission android:name="android.permission.INTERNET"/>
<uses-permission android:name="android.permission.RECEIVE_BOOT_COMPLETED"/>
<uses-permission android:name="android.permission.POST_NOTIFICATIONS"/>`}
                        </CodeBlock>
                    </Box>
                    <Box mt={4}>
                        <Text fontWeight="medium" mb={2}>Application Configuration</Text>
                        <Text fontWeight="normal" mb={2}>Modify the <Code>&lt;application&gt;</Code> (of your manifest) tag to include necessary services and activities:</Text>
                        <CodeBlock language="xml" section="manifest">
                            {`<manifest xmlns:android="http://schemas.android.com/apk/res/android"
    xmlns:tools="http://schemas.android.com/tools">
    
    <application
    android:name=".XpushApp"
    android:allowBackup="false"
    tools:replace="android:theme">

    <activity
        android:name="com.example.xnotifysdktest.activities.DashboardActivity" // replace activity open push 
        android:exported="true"
        android:launchMode="singleTop">
        <intent-filter>
            <action android:name="com.example.xnotifysdktest.OPEN_DASHBOARD" />
            <category android:name="android.intent.category.DEFAULT" />
        </intent-filter>
    </activity>

    <service
    android:name="com.xpush.sdk.MyFirebaseMessagingService"
    android:exported="false">
    <intent-filter>
        <action android:name="com.google.firebase.MESSAGING_EVENT"/>
    </intent-filter>
    </service>

</application>
</manifest>`}
                        </CodeBlock>
                    </Box>
                </Card>

                {/* Step 8 */}
                <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                    <Heading as="h3" size="md" mb={4}>8. Add XpushApp Class for Background Launch</Heading>
                    <Text mb={2}>Create a new class <Code>XpushApp</Code> to handle the app launch activity when in the background:</Text>
                    <CodeBlock language="java" section="xpush-app">
                        {`package com.example.xnotifysdktest

import android.app.Activity
import android.app.Application
import android.widget.TextView
import com.example.xnotifysdktest.activities.DashboardActivity
import com.xpush.sdk.NotificationManager
import com.xpush.sdk.MyFirebaseMessagingService
import android.util.Log
import com.xpush.sdk.NotificationMainActivity
import com.xpush.sdk.utils.MMKVUtils

class XpushApp : Application() {

    override fun onCreate() {
        super.onCreate()
        MMKVUtils.initSafely(this)
       MyFirebaseMessagingService.launchActivity = DashboardActivity::class.java
 

    }

    fun initializeSDK(activity: Activity, userId: String) {
        if (userId.isNotEmpty()) {


            val property_id = "" // add client key

            MyFirebaseMessagingService.notificationIconResId = R.drawable.ic_list_notification
            MyFirebaseMessagingService.launchActivity = DashboardActivity::class.java
          
            val counterView = activity.findViewById<TextView>(R.id.notification_counter)

            NotificationManager.initialize(activity, userId, property_id,counterView)

            NotificationMainActivity.icons = listOf(
                com.xpush.sdk.R.drawable.ic_bell, com.xpush.sdk.R.drawable.ic_offer,
                com.xpush.sdk.R.drawable.ic_bell)
            NotificationMainActivity.tabs = listOf("Notifications", "Promotions","Information")

           
        }
    }

}`}
                    </CodeBlock>
                </Card>
            </VStack>
        </div>
    );
};

export default Kotlin;